// FulfillmentOrdersPanel — Slice 3 customer-order pipeline.
// Retail-side order tracking over ops_order_intake rows this org submitted.

import type { OpsOrderIntakeStatus } from "@/lib/orderIntakeClient";
import type { FulfillmentOrderRow } from "@/lib/fulfillment/fulfillmentApi";
import { useFulfillmentOrders, type FulfillmentOrdersView } from "./useFulfillmentOrders";

const STATUS_LABEL: Record<OpsOrderIntakeStatus, string> = {
  pending_review: "Pending review",
  on_hold: "On hold",
  accepted: "Accepted",
  cancelled: "Cancelled",
};

const STATUS_CLASS: Record<OpsOrderIntakeStatus, string> = {
  pending_review:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  on_hold: "bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-100",
  accepted: "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100",
  cancelled: "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-300",
};

function StatusBadge({ status }: { status: OpsOrderIntakeStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_CLASS[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function ThreadLinks({ order }: { order: FulfillmentOrderRow }) {
  if (!order.lead_id && !order.linked_opportunity_id && !order.materialized_deal_id) {
    return null;
  }
  return (
    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px] text-gray-500 dark:text-zinc-500">
      {order.lead_id ? <span>lead {order.lead_id.slice(0, 8)}</span> : null}
      {order.linked_opportunity_id ? (
        <span>opp {order.linked_opportunity_id.slice(0, 8)}</span>
      ) : null}
      {order.materialized_deal_id ? (
        <span>deal {order.materialized_deal_id.slice(0, 8)}</span>
      ) : null}
    </p>
  );
}

function OrderRow({ order }: { order: FulfillmentOrderRow }) {
  return (
    <li className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-zinc-700 dark:bg-zinc-950/50">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm text-gray-900 dark:text-zinc-100">{order.display_label}</p>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
            {order.destination_label}
            {order.proposed_sale_path ? ` · ${order.proposed_sale_path.replace(/_/g, " ")}` : ""}
            {` · ${order.source.replace(/_/g, " ")}`}
          </p>
          {order.hold_reason ? (
            <p className="mt-1 text-xs text-orange-800 dark:text-orange-200">
              Hold: {order.hold_reason}
            </p>
          ) : null}
          <ThreadLinks order={order} />
        </div>
        <StatusBadge status={order.status} />
      </div>
    </li>
  );
}

function ViewTabs(props: {
  view: FulfillmentOrdersView;
  onChange: (v: FulfillmentOrdersView) => void;
}) {
  const tabs: { id: FulfillmentOrdersView; label: string }[] = [
    { id: "open", label: "Open" },
    { id: "accepted", label: "Accepted" },
    { id: "all", label: "All" },
  ];
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => props.onChange(t.id)}
          className={
            props.view === t.id
              ? "rounded-full bg-indigo-600 px-3 py-1 font-semibold text-white"
              : "rounded-full bg-gray-100 px-3 py-1 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function FulfillmentOrdersPanel() {
  const m = useFulfillmentOrders();
  const pageCount =
    m.total != null && m.total > 0
      ? Math.ceil(m.total / m.pageSize)
      : m.orders.length > 0
        ? m.page + 1
        : 0;
  const canPrev = m.page > 0;
  const canNext =
    m.total != null ? (m.page + 1) * m.pageSize < m.total : m.orders.length === m.pageSize;

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Fulfillment — Orders</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-400">
            Customer orders your org submitted — internal review queue and upstream manufacturer
            submissions. Accepted rows link to the Ops deal spine when materialized.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void m.refresh()}
          disabled={m.busy}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          {m.busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <ViewTabs view={m.view} onChange={m.setView} />

      {m.error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.error}
        </div>
      ) : null}

      {!m.error && !m.busy && m.orders.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-500">
          No orders in this view. Submit from Sales → Order Intake or wait for webhook intake to
          appear here.
        </p>
      ) : null}

      {m.orders.length > 0 ? (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-zinc-800 dark:border-zinc-700">
          {m.orders.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
        </ul>
      ) : null}

      {pageCount > 1 || canNext || canPrev ? (
        <div className="flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
          <button
            type="button"
            className="underline disabled:opacity-40"
            disabled={!canPrev || m.busy}
            onClick={() => m.setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {m.page + 1}
            {m.total != null ? ` of ${pageCount}` : ""}
          </span>
          <button
            type="button"
            className="underline disabled:opacity-40"
            disabled={!canNext || m.busy}
            onClick={() => m.setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
